import React, { useEffect, useState, memo } from 'react';
import { X, ArrowRight, Check, Sparkles } from 'lucide-react';
type TourStep = {
  id: string;
  title: string;
  description: string;
  targetSelector: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  action?: 'click' | 'navigate';
  nextStep?: string;
};
const tourSteps: TourStep[] = [{
  id: 'sidebar-stories',
  title: 'Navigate to Stories',
  description: 'Click on "Stories" in the sidebar to see your recorded stories.',
  targetSelector: '[data-tour="sidebar-stories"]',
  position: 'right',
  action: 'click'
}, {
  id: 'record-button',
  title: 'Record a Story',
  description: 'Click the green "Record" button to start capturing a memory.',
  targetSelector: '[data-tour="record-button"]',
  position: 'bottom',
  action: 'click'
}, {
  id: 'sidebar-create',
  title: 'Go to Create',
  description: 'Click "Create" in the sidebar to transform stories into storybooks.',
  targetSelector: '[data-tour="sidebar-create"]',
  position: 'right',
  action: 'click'
}, {
  id: 'story-card',
  title: 'Select a Story',
  description: 'Click on any story card to create a storybook from it.',
  targetSelector: '[data-tour="story-card"]',
  position: 'top',
  action: 'click'
}, {
  id: 'sidebar-creations',
  title: 'View Creations',
  description: 'Click "Creations" to see your finished storybooks.',
  targetSelector: '[data-tour="sidebar-creations"]',
  position: 'right',
  action: 'click'
}, {
  id: 'storybook-card',
  title: 'Read Your Storybook',
  description: 'Click on any storybook to read and share it.',
  targetSelector: '[data-tour="storybook-card"]',
  position: 'top',
  action: 'click'
}];
type InteractiveTourProps = {
  isActive: boolean;
  onComplete: () => void;
  onSkip: () => void;
  currentPage: string;
};
export function InteractiveTour({
  isActive,
  onComplete,
  onSkip,
  currentPage
}: InteractiveTourProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({
    top: 0,
    left: 0
  });
  const currentStep = tourSteps[currentStepIndex];
  useEffect(() => {
    if (!isActive || !currentStep) return;
    // Find the target element
    const element = document.querySelector(currentStep.targetSelector) as HTMLElement;
    if (element) {
      setTargetElement(element);
      // Calculate tooltip position
      const rect = element.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      let top = 0;
      let left = 0;
      switch (currentStep.position) {
        case 'right':
          top = rect.top + scrollTop + rect.height / 2;
          left = rect.right + scrollLeft + 20;
          break;
        case 'left':
          top = rect.top + scrollTop + rect.height / 2;
          left = rect.left + scrollLeft - 320;
          break;
        case 'top':
          top = rect.top + scrollTop - 180;
          left = rect.left + scrollLeft + rect.width / 2 - 150;
          break;
        case 'bottom':
          top = rect.bottom + scrollTop + 20;
          left = rect.left + scrollLeft + rect.width / 2 - 150;
          break;
      }
      setTooltipPosition({
        top,
        left
      });
      // Add click listener to advance tour
      const handleClick = (e: MouseEvent) => {
        if (currentStep.action === 'click') {
          e.stopPropagation();
          handleNext();
        }
      };
      element.addEventListener('click', handleClick, true);
      return () => element.removeEventListener('click', handleClick, true);
    }
  }, [currentStepIndex, isActive, currentStep]);
  const handleNext = () => {
    if (currentStepIndex < tourSteps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      onComplete();
    }
  };
  const handleSkip = () => {
    onSkip();
  };
  if (!isActive || !currentStep || !targetElement) return null;
  const rect = targetElement.getBoundingClientRect();
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  return <>
      {/* Dark overlay with cutout for highlighted element */}
      <div className="fixed inset-0 z-[100] pointer-events-none" style={{
      background: `
            linear-gradient(to right, 
              rgba(0,0,0,0.6) 0%, 
              rgba(0,0,0,0.6) ${rect.left + scrollLeft - 8}px,
              transparent ${rect.left + scrollLeft - 8}px,
              transparent ${rect.right + scrollLeft + 8}px,
              rgba(0,0,0,0.6) ${rect.right + scrollLeft + 8}px,
              rgba(0,0,0,0.6) 100%
            ),
            linear-gradient(to bottom,
              rgba(0,0,0,0.6) 0%,
              rgba(0,0,0,0.6) ${rect.top + scrollTop - 8}px,
              transparent ${rect.top + scrollTop - 8}px,
              transparent ${rect.bottom + scrollTop + 8}px,
              rgba(0,0,0,0.6) ${rect.bottom + scrollTop + 8}px,
              rgba(0,0,0,0.6) 100%
            )
          `
    }} data-id="element-1167" />

      {/* Clickable dark overlay that closes tour */}
      <div className="fixed inset-0 z-[100] cursor-pointer" onClick={handleSkip} style={{
      clipPath: `polygon(
            0% 0%,
            0% 100%,
            100% 100%,
            100% 0%,
            0% 0%,
            0% ${rect.top + scrollTop - 8}px,
            ${rect.left + scrollLeft - 8}px ${rect.top + scrollTop - 8}px,
            ${rect.left + scrollLeft - 8}px ${rect.bottom + scrollTop + 8}px,
            ${rect.right + scrollLeft + 8}px ${rect.bottom + scrollTop + 8}px,
            ${rect.right + scrollLeft + 8}px ${rect.top + scrollTop - 8}px,
            100% ${rect.top + scrollTop - 8}px,
            100% 0%
          )`
    }} data-id="element-1168" />

      {/* Green border around highlighted element */}
      <div className="fixed z-[101] border-4 border-green-500 rounded-xl pointer-events-none" style={{
      top: rect.top + scrollTop - 8,
      left: rect.left + scrollLeft - 8,
      width: rect.width + 16,
      height: rect.height + 16,
      transition: 'all 0.3s ease',
      boxShadow: '0 0 0 4px rgba(34, 197, 94, 0.2)'
    }} data-id="element-1169" />

      {/* Pulsing ring animation */}
      <div className="fixed border-4 border-green-400 rounded-xl animate-ping opacity-75 pointer-events-none z-[101]" style={{
      top: rect.top + scrollTop - 8,
      left: rect.left + scrollLeft - 8,
      width: rect.width + 16,
      height: rect.height + 16
    }} data-id="element-1170" />

      {/* Clickable area overlay - positioned exactly over the target */}
      <div className="fixed z-[102] cursor-pointer" style={{
      top: rect.top + scrollTop,
      left: rect.left + scrollLeft,
      width: rect.width,
      height: rect.height,
      transition: 'all 0.3s ease'
    }} onClick={() => {
      targetElement.click();
      handleNext();
    }} data-id="element-1171" />

      {/* Tooltip */}
      <div className="fixed z-[103] pointer-events-auto" style={{
      top: tooltipPosition.top,
      left: tooltipPosition.left,
      transition: 'all 0.3s ease'
    }} data-id="element-1172">
        <div className="bg-white rounded-2xl shadow-2xl p-6 w-80 border-2 border-green-500" data-id="element-1173">
          {/* Close button */}
          <button onClick={handleSkip} className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors border-2 border-gray-200" data-id="element-1174">
            <X className="w-4 h-4 text-gray-600" data-id="element-1175" />
          </button>

          {/* Progress */}
          <div className="flex items-center gap-2 mb-4" data-id="element-1176">
            {tourSteps.map((_, index) => <div key={index} className={`h-1.5 rounded-full transition-all ${index === currentStepIndex ? 'bg-green-500 w-8' : index < currentStepIndex ? 'bg-green-300 w-6' : 'bg-gray-200 w-6'}`} data-id="element-1177" />)}
          </div>

          {/* Content */}
          <div className="mb-4" data-id="element-1178">
            <div className="flex items-center gap-2 mb-2" data-id="element-1179">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-1180">
                <Sparkles className="w-4 h-4 text-green-600" data-id="element-1181" />
              </div>
              <h3 className="font-bold text-gray-900" data-id="element-1182">{currentStep.title}</h3>
            </div>
            <p className="text-gray-600 text-sm" data-id="element-1183">{currentStep.description}</p>
          </div>

          {/* Action hint */}
          <div className="flex items-center justify-between" data-id="element-1184">
            <div className="text-xs text-gray-500" data-id="element-1185">
              Step {currentStepIndex + 1} of {tourSteps.length}
            </div>
            <div className="flex items-center gap-2 text-sm font-medium text-green-600" data-id="element-1186">
              <span data-id="element-1187">Click to continue</span>
              <ArrowRight className="w-4 h-4" data-id="element-1188" />
            </div>
          </div>

          {/* Skip button */}
          <button onClick={handleSkip} className="w-full mt-4 py-2 text-gray-600 text-sm hover:text-gray-900 transition-colors" data-id="element-1189">
            Skip Tour
          </button>
        </div>

        {/* Arrow pointer */}
        {currentStep.position === 'right' && <div className="absolute left-0 top-1/2 -translate-x-3 -translate-y-1/2 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-green-500" data-id="element-1190"></div>}
        {currentStep.position === 'left' && <div className="absolute right-0 top-1/2 translate-x-3 -translate-y-1/2 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-l-8 border-l-green-500" data-id="element-1191"></div>}
        {currentStep.position === 'top' && <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-3 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-green-500" data-id="element-1192"></div>}
        {currentStep.position === 'bottom' && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-3 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-b-green-500" data-id="element-1193"></div>}
      </div>
    </>;
}